import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getReport } from "../lib/api";
import { STATUS_META } from "../lib/specs";
import { Printer, ArrowRight, Loader2, AlertTriangle } from "lucide-react";

const SnapTable = ({ snap, title }) => (
  <div className="rounded-xl border border-white/10 bg-surface p-5" data-testid={`view-${title.toLowerCase()}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="font-mono text-[11px] text-slate-400 tracking-widest uppercase">{title}</span>
      {snap && <span className="font-mono text-sm font-bold" style={{ color: snap.passRate >= 80 ? "#10B981" : snap.passRate >= 50 ? "#F59E0B" : "#EF4444" }}>{snap.passRate}%</span>}
    </div>
    {!snap ? <p className="font-mono text-[11px] text-slate-600">—</p> : (
      <div className="space-y-1.5">
        {snap.rows.filter((x) => x.status !== "empty").map((x, i) => {
          const meta = STATUS_META[x.status] || STATUS_META.empty;
          return (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-slate-400">{x.label}</span>
              <span className="flex items-center gap-2">
                <span className="font-mono tabular-nums text-white">{x.value}°</span>
                <span className="font-mono text-[9px] px-1.5 py-0.5 rounded" style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
              </span>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default function ReportView() {
  const { id } = useParams();
  const [state, setState] = useState("loading");
  const [rep, setRep] = useState(null);

  useEffect(() => {
    getReport(id).then((d) => { setRep(d); setState("ok"); }).catch(() => setState("error"));
  }, [id]);

  if (state === "loading") {
    return <div className="bg-ink min-h-screen flex items-center justify-center"><Loader2 size={32} className="animate-spin text-cyan" /></div>;
  }
  if (state === "error") {
    return (
      <div className="bg-ink min-h-screen flex items-center justify-center text-center px-4">
        <div>
          <AlertTriangle size={40} className="mx-auto text-amber" />
          <h1 className="mt-4 font-heading font-bold text-xl text-white">Report not found</h1>
          <Link to="/" className="mt-4 inline-block font-mono text-xs text-cyan uppercase tracking-widest">← Back to Thortek</Link>
        </div>
      </div>
    );
  }

  const date = rep.createdAt ? new Date(rep.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";

  return (
    <div className="bg-ink text-white min-h-screen" data-testid="report-view-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-3">
            <img src="/thortek-logo.png" alt="Thortek Performance" className="h-8 w-auto" />
          </Link>
          <button onClick={() => window.print()} data-testid="view-print" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-slate-300 hover:text-cyan hover:border-cyan/50 font-mono text-xs uppercase tracking-widest transition-all print:hidden">
            <Printer size={15} /> Print / PDF
          </button>
        </div>

        <div className="border-b border-white/10 pb-6 mb-6">
          <div className="font-mono text-[11px] text-cyan tracking-widest uppercase">Alignment Report</div>
          <h1 className="mt-2 font-heading font-black tracking-tight text-3xl">{rep.vehicleLabel || "Vehicle"}</h1>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px] text-slate-500 uppercase tracking-widest">
            <span>{date}</span>
            {rep.techName && <span>Tech: {rep.techName}</span>}
            <span>ID: {rep.id}</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <SnapTable snap={rep.before} title="Before" />
          <SnapTable snap={rep.after} title="After" />
        </div>

        {rep.notes && (
          <div className="mt-6 rounded-xl border border-white/10 bg-surface p-5">
            <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-2">Notes</div>
            <p className="text-sm text-slate-200 leading-relaxed">{rep.notes}</p>
          </div>
        )}

        <div className="mt-10 rounded-xl border border-cyan/30 bg-gradient-to-br from-cyan/10 to-transparent p-6 text-center print:hidden">
          <p className="font-heading font-bold text-lg">Measured with Thortek Performance</p>
          <p className="mt-1 text-sm text-slate-400">Shop-grade alignment from your phone — camber, caster, toe, AI scan and more.</p>
          <Link to="/#pricing" data-testid="view-cta" className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan text-ink font-heading font-bold uppercase tracking-wide text-sm hover:bg-cyan-hover transition-all">
            Get the app — $199 <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
